<template>
    <BaseDialog v-model:open="open" :title="action.title">
        <div class="flex min-w-0 flex-col gap-1">
            <span class="scaffold-details-bold text-ink-muted">{{ channelPresentation.label }}</span>
            <WeeklyActionTypeChip :channel="action.channel" appearance="journey" label-mode="participation" />
        </div>

        <section v-if="action.details" class="flex flex-col gap-2">
            <h3 class="scaffold-details-bold text-ink">{{ t("developmentJourneys.weeklyActionDetailsDialog.details") }}</h3>
            <p class="body-small text-ink-muted">{{ action.details }}</p>
        </section>

        <section v-if="action.justification" class="flex flex-col gap-2">
            <h3 class="scaffold-details-bold text-ink">{{ t("developmentJourneys.weeklyActionDetailsDialog.whyNadiaSuggested") }}</h3>
            <p class="body-small text-ink-muted">{{ action.justification }}</p>
        </section>

        <section v-if="action.evidence_groups?.length" class="flex flex-col gap-2">
            <h3 class="scaffold-details-bold text-ink">{{ t("developmentJourneys.weeklyActionDetailsDialog.basedOn") }}</h3>
            <InsightEvidenceChips :evidence-groups="action.evidence_groups ?? []" :show-label="false" />
        </section>

        <section v-if="action.development_goal" class="flex flex-col gap-2">
            <h3 class="scaffold-details-bold text-ink">{{ t("developmentJourneys.weeklyActionDetailsDialog.supportingContext") }}</h3>
            <div class="flex flex-wrap gap-2">
                <SourceChip v-if="action.development_goal" source="journeys" size="xs" class="max-w-full">
                    <span class="min-w-0 truncate">{{ action.development_goal.title }}</span>
                </SourceChip>
            </div>
        </section>

        <p v-if="!action.details && !action.justification && !action.development_goal && !action.evidence_groups?.length" class="body-small text-ink-muted">
            {{ t("developmentJourneys.weeklyActionDetailsDialog.noAdditionalDetails") }}
        </p>

        <BaseNotification v-if="conversationError" data-testid="weekly-action-conversation-error" theme="error" :text="conversationError" hide-dismiss />

        <template v-if="showPrimaryAction && primaryAction && action.status !== 'completed'" #footer>
            <a
                v-if="primaryAction.kind === 'link'"
                data-testid="weekly-action-details-resource-link"
                :href="primaryAction.href"
                :target="primaryAction.target"
                :rel="primaryAction.rel"
                class="base-btn-primary base-btn-sm inline-flex items-center justify-center rounded-lg"
                @click="handlePrimaryActionLinkClick">
                {{ primaryAction.ctaLabel }}
            </a>
            <BaseButton
                v-else
                data-testid="weekly-action-details-start-conversation"
                type="button"
                theme="primary"
                size="sm"
                :disabled="isConversationLaunching"
                :aria-busy="isConversationLaunching"
                @click="startConversation">
                {{ primaryAction.ctaLabel }}
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import InsightEvidenceChips from "~vue/Labs/InsightEvidenceChips.vue";
import WeeklyActionTypeChip from "~vue/pages/developmentJourneys/WeeklyActionTypeChip.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { getWeeklyActionChannelPresentation } from "./weeklyActionPresentation";
import { getWeeklyActionPrimaryAction, handleWeeklyActionPrimaryLinkClick, logWeeklyActionPrimaryActionClicked } from "./weeklyActionPrimaryAction";
import type { WeeklyAction } from "./weeklyActions";

const props = defineProps<{
    action: WeeklyAction;
    rolePlaySetupUrl: string;
    showPrimaryAction: boolean;
    isConversationLaunching?: boolean;
    conversationError?: string | null;
}>();

const emit = defineEmits<{
    "start-conversation": [initialUserMessage: string];
}>();

const { t } = useI18n();

const open = defineModel<boolean>("open", { default: false });
const channelPresentation = computed(() => getWeeklyActionChannelPresentation(props.action.channel));
const primaryAction = computed(() => getWeeklyActionPrimaryAction(props.action, props.rolePlaySetupUrl));

function handlePrimaryActionLinkClick(event: MouseEvent) {
    const action = primaryAction.value;
    if (action?.kind === "link") {
        void handleWeeklyActionPrimaryLinkClick(action, event);
    }
}

function startConversation() {
    if (props.isConversationLaunching) {
        return;
    }
    const action = primaryAction.value;
    if (action?.kind !== "start_conversation") {
        return;
    }
    logWeeklyActionPrimaryActionClicked(action);
    emit("start-conversation", action.initialUserMessage);
}
</script>
